import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  computed,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';

/**
 * Handle height from Figma (Tex_Icon 17.306×19.674).
 */
const HANDLE = 19.6742;

/**
 * A scroll container that draws the console's own scrollbar — a 4px track, an EchoBlue fill up
 * to the SpaceViolet hexagon handle — instead of the native one. Give it a height or max-height;
 * wheel, touch, keyboard (when focused), handle drag and track clicks all scroll it.
 */
@Component({
  selector: 'esn-scroll-area',
  templateUrl: './scroll-area.component.html',
  styleUrl: './scroll-area.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScrollAreaComponent {
  /**
   * Accessible name of the scrollable region (it is keyboard-focusable).
   */
  readonly label = input.required<string>();

  private readonly viewport = viewChild.required<ElementRef<HTMLElement>>('viewport');
  private readonly content = viewChild.required<ElementRef<HTMLElement>>('content');

  private readonly scrollTop = signal(0);
  private readonly scrollRange = signal(0);
  private readonly railHeight = signal(0);
  private dragStart: { y: number; top: number } | null = null;

  protected readonly overflowing = computed(() => this.scrollRange() > 0);
  protected readonly handleTop = computed(() => {
    const range = this.scrollRange();
    const travel = Math.max(0, this.railHeight() - HANDLE);
    return range > 0 ? (this.scrollTop() / range) * travel : 0;
  });
  protected readonly fillHeight = computed(() => this.handleTop() + HANDLE / 2);

  constructor() {
    const destroyRef = inject(DestroyRef);
    afterNextRender(() => {
      this.measure();
      if (typeof ResizeObserver === 'undefined') return;
      const observer = new ResizeObserver(() => this.measure());
      observer.observe(this.viewport().nativeElement);
      observer.observe(this.content().nativeElement);
      destroyRef.onDestroy(() => observer.disconnect());
    });
  }

  /**
   * Re-reads the viewport's scroll geometry (on scroll and on any size change).
   */
  measure(): void {
    const el = this.viewport().nativeElement;
    this.scrollTop.set(el.scrollTop);
    this.scrollRange.set(Math.max(0, el.scrollHeight - el.clientHeight));
    this.railHeight.set(el.clientHeight);
  }

  // The handle stops its own pointerdown, so this only fires for clicks on the track.
  protected jumpTo(event: PointerEvent): void {
    const rail = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.scrollToHandleTop(event.clientY - rail.top - HANDLE / 2);
  }

  protected startDrag(event: PointerEvent): void {
    event.stopPropagation();
    (event.currentTarget as Element).setPointerCapture?.(event.pointerId);
    this.dragStart = { y: event.clientY, top: this.handleTop() };
  }

  protected drag(event: PointerEvent): void {
    if (this.dragStart) this.scrollToHandleTop(this.dragStart.top + event.clientY - this.dragStart.y);
  }

  protected endDrag(event: PointerEvent): void {
    (event.currentTarget as Element).releasePointerCapture?.(event.pointerId);
    this.dragStart = null;
  }

  private scrollToHandleTop(top: number): void {
    const travel = Math.max(1, this.railHeight() - HANDLE);
    const ratio = Math.min(1, Math.max(0, top / travel));
    const el = this.viewport().nativeElement;
    el.scrollTop = ratio * this.scrollRange();
    this.measure();
  }
}
